import { ArrowRightIcon } from 'lucide-react'
import React from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGetDentalPlans } from 'components/hooks/member_onboarding_v2/useGetDentalPlans'
import { type DentalPlan, type StepComponentProps, type VisionPlan } from 'components/member_onboarding_v2/types'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment } from 'utils/web_api'

import DentalPlanCard from '../components/DentalPlanCard'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import { useOnboarding } from '../OnboardingContext'

const RENAISSANCE_PROVIDER_DIRECTORY_URL =
  'https://www.myrenproviders.com/provider-search/home'
const GUARDIAN_PROVIDER_DIRECTORY_URL =
  'https://www.guardiananytime.com/fpapp/search'
const BSCA_PROVIDER_DIRECTORY_URL = 'https://www.blueshieldca.com/fad/home'

export function DentalPlansPage ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { onboardingData, enrollmentEventId, deleteEnrollment, planChangeSummary } =
    useOnboarding()
  const {
    data: dentalPlanData,
    isLoading,
    error,
  } = useGetDentalPlans({ enrollmentEventId })
  const [submitError, setSubmitError] =
    React.useState<GrapeAPIErrorType | null>(null)

  const { breakpointIsAtLeast } = useBreakpoint()

  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        onHandleError(error as GrapeAPIErrorType)
      },
    },
  })

  const availablePlans = dentalPlanData?.data ?? []
  const currentPlan =
    onboardingData?.enrollments?.find((e) => e.covers_dental) || null
  const renewalPlanCode = planChangeSummary?.next_plan_year.dental_plan?.plan_code || null
  let renewalPlan = null
  if (renewalPlanCode) {
    renewalPlan = availablePlans.find((plan) => plan.plan_code === renewalPlanCode)
  }

  const deleteMutation = deleteEnrollment(currentPlan?.id || '')

  const renaissanceAvailable = availablePlans.some(
    (plan) => plan.carrier.toLowerCase() === 'renaissance'
  )
  const guardianAvailable = availablePlans.some(
    (plan) => plan.carrier.toLowerCase() === 'guardian'
  )
  const bscaAvailable = availablePlans.some(
    (plan) => plan.carrier.toLowerCase() === 'blueshield of california'
  )

  const getDentalPlansDescription = () => {
    if (availablePlans.length === 0) {
      return 'Your employer is offering you coverage for dental plans, but unfortunately no plans are available in you area.'
    }

    let providerNames = ''
    let inNetworkDentistLists = null

    const availableProviders = []
    const providerLinks = []
    if (renaissanceAvailable) {
      availableProviders.push('Renaissance')
      providerLinks.push(
        <a key='renaissance' href={RENAISSANCE_PROVIDER_DIRECTORY_URL}>
          Renaissance's provider directory
        </a>
      )
    }
    if (guardianAvailable) {
      availableProviders.push('Guardian')
      providerLinks.push(
        <a key='guardian' href={GUARDIAN_PROVIDER_DIRECTORY_URL}>
          Guardian's provider directory
        </a>
      )
    }
    if (bscaAvailable) {
      availableProviders.push('BlueShield of California')
      providerLinks.push(
        <a key='bsca' href={BSCA_PROVIDER_DIRECTORY_URL}>
          BlueShield of California's provider directory
        </a>
      )
    }

    // Format provider names
    if (availableProviders.length === 1) {
      providerNames = availableProviders[0]
    } else if (availableProviders.length === 2) {
      providerNames = `${availableProviders[0]} and ${availableProviders[1]}`
    } else if (availableProviders.length > 2) {
      const lastProvider = availableProviders.pop()
      providerNames = `${availableProviders.join(', ')}, and ${lastProvider}`
    }

    // Format provider links
    if (providerLinks.length === 1) {
      inNetworkDentistLists = providerLinks[0]
    } else if (providerLinks.length === 2) {
      inNetworkDentistLists = (
        <>
          {providerLinks[0]}
          {' or '}
          {providerLinks[1]}
        </>
      )
    } else if (providerLinks.length > 2) {
      inNetworkDentistLists = (
        <>
          {providerLinks.map((link, index) => (
            <React.Fragment key={index}>
              {link}
              {index < providerLinks.length - 2 && ', '}
              {index === providerLinks.length - 2 && ', or '}
            </React.Fragment>
          ))}
        </>
      )
    }

    return (
      <>
        <p>
          Dental plans in your area are available through {providerNames}. We'll
          enroll you and any dependents using the same information you provide
          for your medical plans.
        </p>
        <br />
        <p>You can search for in-network dentists at {inNetworkDentistLists}.</p>
      </>
    )
  }

  const onHandleError = (error: GrapeAPIErrorType) => {
    setSubmitError(error)
  }

  const onSelectPlan = (plan: DentalPlan | VisionPlan) => {
    createEnrollment.mutate({
      enrollmentEventId,
      data: {
        plan_code: plan.plan_code,
        carrier_name: plan.carrier,
        state: 'in_member_cart',
        covers_medical: false,
        covers_dental: true,
        covers_vision: false,
        quoted_premium: Number(plan.premium.subunits / 100),
      },
    })
  }

  const onRemovePlan = () => {
    deleteMutation.mutate()
    navigateToNextStep?.()
  }

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return <PageErrorBanner error={error} />
  }
  if (submitError) {
    return (
      <Banner
        title='A problem has occurred while processing your request'
        type='critical'
      >
        <OnboardingGenericErrorMessage error={submitError} />
      </Banner>
    )
  }

  function renderButtons () {
    const isMobile = !breakpointIsAtLeast('xs')

    const backButton = (
      <Button
        size='large'
        variant='secondary'
        onClick={() => navigateToPreviousStep?.()}
      >
        Back
      </Button>
    )

    const removeButton = (
      <Button
        data-testid='remove-plan-button'
        size='large'
        variant='tertiary-deemphasized'
        onClick={() => onRemovePlan()}
      >
        I don't want a dental plan
      </Button>
    )

    const continueButton = (
      <Button
        size='large'
        variant='primary'
        rightIcon={ArrowRightIcon}
        onClick={() => navigateToNextStep()}
      >
        Continue
      </Button>
    )

    const skipButton = (
      <Button
        variant='tertiary-deemphasized'
        onClick={() => navigateToNextStep()}
        size='large'
      >
        Skip dental
      </Button>
    )

    if (currentPlan) {
      return isMobile
        ? (
          <ButtonGroup layout='vertical' size='large'>
            {continueButton}
            {backButton}
            {removeButton}
          </ButtonGroup>
          )
        : (
          <div className='flex items-center justify-between'>
            {backButton}
            <ButtonGroup
              size='large'
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              {removeButton}
              {continueButton}
            </ButtonGroup>
          </div>
          )
    }

    return isMobile
      ? (
        <ButtonGroup size='large' layout='vertical'>
          {backButton}
          {skipButton}
        </ButtonGroup>
        )
      : (
        <div className='flex items-center justify-between gap-2'>
          {backButton}
          {skipButton}
        </div>
        )
  }

  return (
    <div data-testid='DentalPlansPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Choose your dental insurance
        </h1>
        <span className='body text-gray-500'>
          {getDentalPlansDescription()}
        </span>
      </div>
      {availablePlans.length > 0 && (
        <div className='flex flex-col mb-10 gap-y-6'>
          {renewalPlan && (
            <div>
              <DentalPlanCard
                badgeCategory='renewal'
                key={`${renewalPlan.carrier}-${renewalPlan.name}`}
                displayedPlan={renewalPlan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
            </div>
          )}
          {availablePlans.map((plan) => {
            if (plan.plan_code === renewalPlanCode) {
              return null
            }
            return (
              <DentalPlanCard
                key={`${plan.carrier}-${plan.name}`}
                displayedPlan={plan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
            )
          })}
        </div>
      )}
      {renderButtons()}
    </div>
  )
}
